// components/public/BeauxBot.tsx
/**
 * @description Beaux AI helper bot - floating button + chat drawer
 * Provides canned responses to common rental questions
 */
import { useState } from 'react';
import { X, MessageCircle, ThumbsUp, ThumbsDown, Mail } from 'lucide-react';
import { MOCK_BUILDING } from '../../lib/mockData';

const BEAUX_QA = [
  {
    id: 'documents',
    question: 'What documents do I need?',
    answer: "To rent, you'll need: valid government ID, proof of income (recent pay stubs or employment letter), and a credit check authorization. New to Canada? We also accept proof of funds—just ask!"
  },
  {
    id: 'pets',
    question: 'Do you accept pets?',
    answer: "Most of our buildings are pet-friendly! There may be a small monthly pet fee. Ask the leasing agent about specific pet policies for this building."
  },
  {
    id: 'rent',
    question: "What's included in rent?",
    answer: 'At this building, rent typically includes water and heating. Hydro (electricity) is usually tenant-paid. Parking and storage are available for an additional fee.'
  },
  {
    id: 'viewing',
    question: 'How do I schedule a viewing?',
    answer: "Tap 'Contact Agent' below to email our leasing team directly. Include your preferred dates and times, and they'll get back to you within 24 hours!"
  }
];

interface Message {
  type: 'question' | 'answer';
  content: string;
}

export function BeauxBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(new Set());
  const [showPulse, setShowPulse] = useState(true);

  const availableQuestions = BEAUX_QA.filter(qa => !answeredIds.has(qa.id));

  const handleQuestionClick = (qa: typeof BEAUX_QA[0]) => {
    setMessages(prev => [
      ...prev,
      { type: 'question', content: qa.question },
      { type: 'answer', content: qa.answer }
    ]);
    setAnsweredIds(prev => new Set([...prev, qa.id]));
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowPulse(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleContactHuman = () => {
    window.location.href = `mailto:${MOCK_BUILDING.leasing_email}?subject=Question about ${MOCK_BUILDING.name}`;
  };

  const resetChat = () => {
    setMessages([]);
    setAnsweredIds(new Set());
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleOpen}
        className={`fixed bottom-20 right-4 w-14 h-14 bg-primary hover:bg-primary-hover text-white rounded-full shadow-lg flex items-center justify-center z-[80] transition-transform hover:scale-105 ${showPulse ? 'animate-pulse-subtle' : ''
          }`}
        aria-label="Open Beaux assistant"
      >
        <div className="relative">
          <MessageCircle size={24} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-primary text-[10px] font-bold rounded-full flex items-center justify-center">
            B
          </span>
        </div>
      </button>

      {/* Chat Drawer */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[100] animate-fade-in"
            onClick={handleClose}
          />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] max-w-[430px] mx-auto animate-slide-up max-h-[70vh] min-h-[400px] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-lg">B</span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Hi! I'm Beaux</h2>
                  <p className="text-xs text-gray-500">Your rental assistant</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-600 mb-1">How can I help?</p>
                  <p className="text-xs text-gray-400">Tap a question below</p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`${msg.type === 'question'
                        ? 'ml-auto bg-primary text-white max-w-[80%]'
                        : 'mr-auto bg-gray-100 text-gray-800 max-w-[85%]'
                      } rounded-2xl px-4 py-3 text-sm`}
                  >
                    {msg.content}
                    {msg.type === 'answer' && (
                      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-200/50">
                        <span className="text-xs text-gray-500">Was this helpful?</span>
                        <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                          <ThumbsUp size={14} className="text-gray-400" />
                        </button>
                        <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                          <ThumbsDown size={14} className="text-gray-400" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Question Buttons */}
            <div className="px-4 pb-2 space-y-2 flex-shrink-0">
              {availableQuestions.length > 0 ? (
                availableQuestions.map((qa) => (
                  <button
                    key={qa.id}
                    onClick={() => handleQuestionClick(qa)}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-sm text-gray-700 transition-colors border border-gray-100"
                  >
                    {qa.question}
                  </button>
                ))
              ) : (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-500 mb-2">
                    Those are all my pre-programmed answers!
                  </p>
                  <button
                    onClick={resetChat}
                    className="text-xs text-primary hover:underline"
                  >
                    Start over
                  </button>
                </div>
              )}
            </div>

            {/* Contact Human Link */}
            <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <button
                onClick={handleContactHuman}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
              >
                <Mail size={16} />
                Or contact leasing team
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
